import { parseHHMMMinutes, toHHMM } from './hhmm';
import type { JourneyJson } from '../types/JourneyJson';
import type { LegJson } from '../types/LegJson';

/** Pre-computed display fields for a journey card. */
export interface JourneySummary {
  /** HH:MM of the first leg's departure. Empty when the journey has no legs or the value is malformed. */
  departure: string;
  /** HH:MM extracted from `journey.arrival`. Empty when the value lacks a `HH:MM` prefix. */
  arrival: string;
  /** Whole minutes between departure and arrival; 0 when either is missing. */
  durationMinutes: number;
  /** Rendered "17 min" or "1h 5min"; hours are dropped when duration < 60. */
  durationLabel: string;
  /** Number of transfers = max(0, transit_leg_count - 1). */
  transferCount: number;
  /** Rendered "1 transfer" / "2 transfers" / "direct". Empty when no transit. */
  transferLabel: string;
  /** Number of transit legs. Walking legs are not counted as transfers. */
  transitLegCount: number;
  /** True when the API tagged this journey as recommended. */
  isRecommended: boolean;
  /** Only the transit legs, in order — for rendering line badges. */
  transitLegs: TransitLeg[];
}

const MIN_PER_HOUR = 60;

function formatDuration(totalMinutes: number): string {
  if (totalMinutes < MIN_PER_HOUR) return `${String(totalMinutes)} min`;
  const hours = Math.floor(totalMinutes / MIN_PER_HOUR);
  const minutes = totalMinutes - hours * MIN_PER_HOUR;
  if (minutes === 0) return `${String(hours)}h`;
  return `${String(hours)}h ${String(minutes)}min`;
}

function formatTransfers(transitCount: number): { count: number; label: string } {
  if (transitCount === 0) return { count: 0, label: '' };
  const count = Math.max(0, transitCount - 1);
  if (count === 0) return { count: 0, label: 'direct' };
  if (count === 1) return { count: 1, label: '1 transfer' };
  return { count, label: `${String(count)} transfers` };
}

/** Narrowed variant for callers that want to render transit-specific fields. */
type TransitLeg = Extract<LegJson, { type: 'transit' }>;

function computeDurationMinutes(firstLeg: LegJson | undefined, arrival: string): number {
  const departureMin = firstLeg ? parseHHMMMinutes(firstLeg.departure) : undefined;
  const arrivalMin = parseHHMMMinutes(arrival);
  if (departureMin === undefined || arrivalMin === undefined) return 0;
  return Math.max(0, arrivalMin - departureMin);
}

/**
 * Derives display-ready fields from a raw journey. Empty-legs journey still
 * gets a valid summary — `departure` and `durationMinutes` fall back to '' / 0.
 */
export function summarize(journey: JourneyJson): JourneySummary {
  const firstLeg = journey.legs.length > 0 ? journey.legs[0] : undefined;
  const arrival = toHHMM(journey.arrival);
  const departure = firstLeg ? toHHMM(firstLeg.departure) : '';
  const durationMinutes = computeDurationMinutes(firstLeg, journey.arrival);
  const transitLegs = journey.legs.filter(
    (legItem): legItem is TransitLeg => legItem.type === 'transit',
  );
  const transfers = formatTransfers(transitLegs.length);
  return {
    departure,
    arrival,
    durationMinutes,
    durationLabel: formatDuration(durationMinutes),
    transferCount: transfers.count,
    transferLabel: transfers.label,
    transitLegCount: transitLegs.length,
    isRecommended: journey.recommended === true,
    transitLegs,
  };
}

export { toHHMM } from './hhmm';
